import { useCallback, useEffect, useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useAuth } from '../contexts/AuthContext';

interface AIConfig {
    enabled: boolean;
    chatModel: string;
    embeddingModel: string;
    temperature: number;
    perUserDailyUsdCap: number;
    globalDailyUsdCap: number;
    killSwitch: boolean;
    systemPrompt: string;
    explainPromptTemplate: string;
    searchPromptTemplate: string;
    updatedAt: string;
    updatedBy: string;
    defaults?: {
        explainPromptTemplate: string;
        searchPromptTemplate: string;
    };
}

export function AdminAIPage() {
    const { tokenReady } = useAuth();
    const [cfg, setCfg] = useState<AIConfig | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState<string | null>(null);
    const [viewDefault, setViewDefault] = useState<{ title: string; field: 'explainPromptTemplate' | 'searchPromptTemplate'; text: string } | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await apiCall<AIConfig>('/manage/ai-config');
            setCfg(r);
            setError(null);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        }
    }, []);

    useEffect(() => {
        if (tokenReady) void load();
    }, [tokenReady, load]);

    async function save(patch: Partial<AIConfig>) {
        if (!cfg) return;
        setSaving(true);
        setError(null);
        try {
            const r = await apiCall<AIConfig>('/manage/ai-config', { method: 'PATCH', body: patch });
            setCfg(r);
            setSavedAt(new Date().toLocaleTimeString());
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSaving(false);
        }
    }

    if (!cfg) {
        return <div className="admin-page">{error ?? 'Loading…'}</div>;
    }

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div>
                    <h1>AI configuration</h1>
                    <p className="admin-page__sub">
                        AI features require an Azure OpenAI deployment (env: <code>AZURE_OPENAI_ENDPOINT</code> +
                        secret <code>azure-openai-key</code> in Key Vault). Until that's set, leaving "Enabled"
                        on will surface 503 to users.
                    </p>
                </div>
            </header>

            {error && <div className="admin-page__error">{error}</div>}
            {savedAt && !error && <div className="form-success">Saved at {savedAt}</div>}

            <div className="form-section">
                <label className="form-group form-group--inline">
                    <input
                        type="checkbox"
                        checked={cfg.enabled}
                        onChange={(e) => void save({ enabled: e.target.checked })}
                        disabled={saving}
                    />
                    <span>Enabled</span>
                </label>
                <label className="form-group form-group--inline">
                    <input
                        type="checkbox"
                        checked={cfg.killSwitch}
                        onChange={(e) => void save({ killSwitch: e.target.checked })}
                        disabled={saving}
                    />
                    <span>Kill switch (overrides enabled)</span>
                </label>
            </div>

            <div className="form-section">
                <label className="form-group">
                    <span className="form-label">Chat deployment name</span>
                    <input
                        type="text"
                        value={cfg.chatModel}
                        onChange={(e) => setCfg({ ...cfg, chatModel: e.target.value })}
                        onBlur={() => void save({ chatModel: cfg.chatModel })}
                        disabled={saving}
                    />
                </label>
                <label className="form-group">
                    <span className="form-label">Embedding deployment name</span>
                    <input
                        type="text"
                        value={cfg.embeddingModel}
                        onChange={(e) => setCfg({ ...cfg, embeddingModel: e.target.value })}
                        onBlur={() => void save({ embeddingModel: cfg.embeddingModel })}
                        disabled={saving}
                    />
                </label>
                <label className="form-group">
                    <span className="form-label">Temperature</span>
                    <input
                        type="number"
                        step={0.1}
                        min={0}
                        max={1}
                        value={cfg.temperature}
                        onChange={(e) => setCfg({ ...cfg, temperature: Number(e.target.value) })}
                        onBlur={() => void save({ temperature: cfg.temperature })}
                        disabled={saving}
                    />
                </label>
                <label className="form-group">
                    <span className="form-label">Per-user daily $ cap</span>
                    <input
                        type="number"
                        step={0.1}
                        min={0}
                        value={cfg.perUserDailyUsdCap}
                        onChange={(e) => setCfg({ ...cfg, perUserDailyUsdCap: Number(e.target.value) })}
                        onBlur={() => void save({ perUserDailyUsdCap: cfg.perUserDailyUsdCap })}
                        disabled={saving}
                    />
                </label>
                <label className="form-group">
                    <span className="form-label">Tenant-wide daily $ cap</span>
                    <input
                        type="number"
                        step={1}
                        min={0}
                        value={cfg.globalDailyUsdCap}
                        onChange={(e) => setCfg({ ...cfg, globalDailyUsdCap: Number(e.target.value) })}
                        onBlur={() => void save({ globalDailyUsdCap: cfg.globalDailyUsdCap })}
                        disabled={saving}
                    />
                </label>
                <label className="form-group">
                    <span className="form-label">System prompt (shared by all features)</span>
                    <textarea
                        rows={5}
                        value={cfg.systemPrompt}
                        onChange={(e) => setCfg({ ...cfg, systemPrompt: e.target.value })}
                        onBlur={() => void save({ systemPrompt: cfg.systemPrompt })}
                        disabled={saving}
                    />
                </label>
            </div>

            <div className="form-section">
                <h3 className="form-section__heading">Per-feature prompts</h3>
                <p className="form-hint">
                    Each prompt is a template. Placeholders are wrapped in <code>{'{braces}'}</code>. Leave blank to use the built-in default.
                </p>
                <div className="form-group">
                    <div className="form-label-row">
                        <span className="form-label">
                            Explain (highlight → AI) — placeholders: <code>{'{path}'}</code>, <code>{'{quote}'}</code>, <code>{'{currentFile}'}</code>, <code>{'{related}'}</code>, <code>{'{relatedCount}'}</code>, <code>{'{userLevel}'}</code>, <code>{'{responseLength}'}</code>
                        </span>
                        {cfg.defaults?.explainPromptTemplate && (
                            <button
                                type="button"
                                className="btn btn--ghost btn--small"
                                onClick={() => setViewDefault({ title: 'Explain — default prompt', field: 'explainPromptTemplate', text: cfg.defaults!.explainPromptTemplate })}
                            >
                                View default
                            </button>
                        )}
                    </div>
                    <textarea
                        rows={8}
                        value={cfg.explainPromptTemplate ?? ''}
                        onChange={(e) => setCfg({ ...cfg, explainPromptTemplate: e.target.value })}
                        onBlur={() => void save({ explainPromptTemplate: cfg.explainPromptTemplate })}
                        disabled={saving}
                        placeholder="Leave blank for built-in default"
                    />
                </div>
                <div className="form-group">
                    <div className="form-label-row">
                        <span className="form-label">
                            Ask AI search — placeholders: <code>{'{query}'}</code>, <code>{'{snippets}'}</code>, <code>{'{userLevel}'}</code>, <code>{'{responseLength}'}</code>
                        </span>
                        {cfg.defaults?.searchPromptTemplate && (
                            <button
                                type="button"
                                className="btn btn--ghost btn--small"
                                onClick={() => setViewDefault({ title: 'Ask AI — default prompt', field: 'searchPromptTemplate', text: cfg.defaults!.searchPromptTemplate })}
                            >
                                View default
                            </button>
                        )}
                    </div>
                    <textarea
                        rows={6}
                        value={cfg.searchPromptTemplate ?? ''}
                        onChange={(e) => setCfg({ ...cfg, searchPromptTemplate: e.target.value })}
                        onBlur={() => void save({ searchPromptTemplate: cfg.searchPromptTemplate })}
                        disabled={saving}
                        placeholder="Leave blank for built-in default"
                    />
                </div>
            </div>

            {viewDefault && (
                <div className="modal-overlay" onClick={() => setViewDefault(null)} role="presentation">
                    <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <header className="modal-header">
                            <h2>{viewDefault.title}</h2>
                            <button className="btn btn--ghost" onClick={() => setViewDefault(null)} aria-label="Close">×</button>
                        </header>
                        <div className="modal-body">
                            <textarea
                                rows={14}
                                readOnly
                                value={viewDefault.text}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <footer className="modal-footer">
                            <button
                                className="btn btn--ghost"
                                onClick={() => {
                                    void navigator.clipboard.writeText(viewDefault.text);
                                }}
                            >
                                Copy to clipboard
                            </button>
                            <button
                                className="btn btn--primary"
                                onClick={() => {
                                    setCfg({ ...cfg, [viewDefault.field]: viewDefault.text } as AIConfig);
                                    void save({ [viewDefault.field]: viewDefault.text } as Partial<AIConfig>);
                                    setViewDefault(null);
                                }}
                            >
                                Use as my prompt
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            <p className="form-hint">
                Last updated by {cfg.updatedBy} at {new Date(cfg.updatedAt).toLocaleString()}.
            </p>
        </div>
    );
}
